// src/pages/reports/ReportBuilder.tsx
import React, { useState, useEffect } from 'react';
import {
  FileText, Download, BarChart3, PieChart as PieChartIcon,
  Table, Calendar, Building2, Tag, Target,
  Play, FileSpreadsheet, Printer, AlertCircle,
  HelpCircle,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Select, LoadingLabel } from '../../components/ui';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';
import { Navigate } from 'react-router-dom';
import { useRole } from '../../hooks/useRole';
import { useAuthStore } from '../../store/authStore';
import { reportService } from '../../services/report';
import toast from 'react-hot-toast';

type ReportType =
  | 'laporan_status'
  | 'laporan_kategori'
  | 'laporan_unit'
  | 'laporan_risiko'
  | 'ppepp_progress'
  | 'aktivitas_pelaporan_unit'
  | 'kinerja_tindak_lanjut_unit'
  | 'tren_bulanan'
  | 'overdue';
type ChartType = 'bar' | 'pie' | 'line' | 'table';

interface ReportConfig {
  type: ReportType;
  label: string;
  description: string;
  icon: React.ReactNode;
  supportedCharts: ChartType[];
}

interface Periode {
  id: number;
  nama: string;
  semester: number;
  tahun_ajaran: string;
}

interface Unit {
  id: number;
  name: string;
  code: string;
  level: number;
  path?: string;
}

const reportConfigs: ReportConfig[] = [
  { type: 'laporan_status', label: 'Laporan per Status', description: 'Distribusi laporan berdasarkan status workflow', icon: <FileText className="w-5 h-5" />, supportedCharts: ['bar', 'pie', 'table'] },
  { type: 'laporan_kategori', label: 'Laporan per Kategori', description: 'Distribusi laporan berdasarkan kategori masalah', icon: <Tag className="w-5 h-5" />, supportedCharts: ['bar', 'pie', 'table'] },
  { type: 'laporan_unit', label: 'Laporan per Unit', description: 'Jumlah laporan berdasarkan unit organisasi', icon: <Building2 className="w-5 h-5" />, supportedCharts: ['bar', 'table'] },
  { type: 'laporan_risiko', label: 'Distribusi Risiko', description: 'Sebaran tingkat risiko masalah', icon: <Target className="w-5 h-5" />, supportedCharts: ['bar', 'pie', 'table'] },
  { type: 'ppepp_progress', label: 'Progress PPEPP', description: 'Status tindak lanjut setelah keputusan RAPIM', icon: <BarChart3 className="w-5 h-5" />, supportedCharts: ['bar', 'pie', 'table'] },
  { type: 'aktivitas_pelaporan_unit', label: 'Aktivitas Pelaporan Unit', description: 'Laporan yang berasal dari suatu unit', icon: <Building2 className="w-5 h-5" />, supportedCharts: ['bar', 'table'] },
  { type: 'kinerja_tindak_lanjut_unit', label: 'Kinerja Tindak Lanjut Unit PIC', description: 'Tindak lanjut yang dikerjakan unit sebagai PIC', icon: <Building2 className="w-5 h-5" />, supportedCharts: ['bar', 'table'] },
  { type: 'tren_bulanan', label: 'Tren Bulanan', description: 'Pergerakan jumlah laporan dari bulan ke bulan', icon: <Calendar className="w-5 h-5" />, supportedCharts: ['line', 'bar', 'table'] },
  { type: 'overdue', label: 'Laporan Melewati Batas Waktu', description: 'Daftar laporan yang melewati target penyelesaian', icon: <FileText className="w-5 h-5" />, supportedCharts: ['table'] },
];

const COLORS = ['#0f33ab', '#32B8C6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#F97316', '#06B6D4', '#6B7280', '#84CC16'];

const InfoTooltipButton: React.FC<{
  title: string;
  content: string;
  align?: 'left' | 'right';
}> = ({ title, content, align = 'right' }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
        aria-label={title}
        aria-expanded={open}
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Tutup penjelasan"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div
            className={`absolute top-full mt-2 z-20 w-80 max-w-[min(20rem,calc(100vw-2rem))] rounded-2xl border border-blue-100 bg-white p-3 shadow-xl ${align === 'left' ? 'left-0' : 'right-0'
              }`}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-blue-700">{title}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-neutral-text-secondary">{content}</p>
          </div>
        </>
      )}
    </div>
  );
};

const ReportBuilder: React.FC = () => {
  const role = useRole();
  const { user } = useAuthStore();

  const [selectedReport, setSelectedReport] = useState<ReportType | null>(null);
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [periode, setPeriode] = useState<string>('all');
  const [unitId, setUnitId] = useState<string>('');
  const [generated, setGenerated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any[]>([]);
  const [periodes, setPeriodes] = useState<Periode[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loadingFilters, setLoadingFilters] = useState(true);

  const currentConfig = reportConfigs.find(r => r.type === selectedReport);
  const isTrendBulananReport = selectedReport === 'tren_bulanan';
  const trendActiveMonths = isTrendBulananReport
    ? reportData.filter((row: any) => Number(row.value || 0) > 0).length
    : 0;
  const hasSingleActiveTrendMonth = isTrendBulananReport && trendActiveMonths <= 1;
  const userUnitName = user?.unit?.name || 'unit Anda';

  const reportExplanation = (() => {
    if (selectedReport === 'aktivitas_pelaporan_unit') {
      if (role.isRektor || role.isAdmin || role.isObserver || role.canManageRapim) {
        return {
          title: 'Cara membaca aktivitas pelaporan',
          content:
            'Laporan ini menunjukkan unit asal laporan. Jika tindak lanjutnya dikerjakan oleh PIC dari unit lain, laporan tetap tercatat pada unit asal agar sumber laporannya tetap jelas.',
        };
      }

      if (role.isWarek) {
        return {
          title: 'Cara membaca aktivitas pelaporan bidang',
          content:
            'Laporan ini membantu melihat dari unit mana laporan pada bidang Anda berasal. Jika tindak lanjutnya dikerjakan unit lain yang masih terkait, laporan tetap tercatat pada unit asalnya.',
        };
      }

      return {
        title: 'Cara membaca aktivitas pelaporan unit',
        content:
          `Laporan ini tetap menghitung unit asal laporan. Jadi jika laporan dari ${userUnitName} ditindaklanjuti oleh PIC dari unit lain, laporan tersebut tetap tercatat pada unit asalnya.`,
      };
    }

    if (selectedReport === 'kinerja_tindak_lanjut_unit') {
      if (role.isRektor || role.isAdmin || role.isObserver || role.canManageRapim) {
        return {
          title: 'Cara membaca kinerja tindak lanjut',
          content:
            'Laporan ini menunjukkan unit yang menerima tugas tindak lanjut sebagai PIC. Karena PIC bisa ditunjuk dari unit lain, unit yang tampil bisa berbeda dari unit asal laporannya.',
        };
      }

      if (role.isWarek) {
        return {
          title: 'Cara membaca tindak lanjut bidang',
          content:
            'Laporan ini membantu melihat unit mana yang benar-benar mengerjakan tindak lanjut pada bidang Anda. Karena PIC bisa ditunjuk dari unit lain, unit yang tampil dapat berbeda dari unit asal laporannya.',
        };
      }

      return {
        title: 'Cara membaca tindak lanjut unit',
        content:
          `Laporan ini mengikuti unit yang ditunjuk sebagai PIC. Karena itu, unit lain bisa tampil di sini jika laporan dari ${userUnitName} dikerjakan oleh PIC dari unit tersebut.`,
      };
    }

    if (selectedReport === 'tren_bulanan') {
      return {
        title: 'Cara membaca tren bulanan',
        content: `Grafik ini menampilkan bulan secara berurutan. Jika pada suatu bulan tidak ada laporan, nilainya tetap ditampilkan sebagai 0 agar pergerakan bulan ke bulan lebih mudah dibaca.${hasSingleActiveTrendMonth
          ? ' Pada periode yang dipilih, data baru muncul pada satu bulan aktif sehingga garis tren masih terlihat pendek.'
          : ''}`,
      };
    }

    return null;
  })();

  useEffect(() => {
    fetchFilters();
  }, []);

  // ========== AUTO-FILTER UNIT UNTUK DEKAN/KALEM ==========
  useEffect(() => {
    if ((role.isDekan || role.isKalem) && user?.unit?.id && !unitId) {
      setUnitId(String(user.unit.id));
    }
  }, [role, user, unitId]);

  const fetchFilters = async () => {
    try {
      const response = await reportService.getFilters();
      let filteredUnits = response.data.units || [];

      // Dekan/Kalem hanya lihat unit sendiri + child units
      if ((role.isDekan || role.isKalem) && user?.unit) {
        const userUnitPath = user.unit.path;
        filteredUnits = filteredUnits.filter((u: Unit) =>
          u.path === userUnitPath ||
          u.path?.startsWith(userUnitPath + '/')
        );
      }

      setPeriodes(response.data.periodes || []);
      setUnits(filteredUnits);
    } catch (error) {
      console.error('Error fetching filters:', error);
      toast.error('Gagal memuat data filter');
    } finally {
      setLoadingFilters(false);
    }
  };

  // ========== ACCESS CONTROL ==========
  if (!role.canAccessReportBuilder) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleGenerate = async () => {
    if (!selectedReport) {
      toast.error('Pilih jenis laporan terlebih dahulu');
      return;
    }
    setLoading(true);
    setGenerated(false);

    try {
      const response = await reportService.getReport(selectedReport, {
        periode_id: periode !== 'all' ? periode : undefined,
        unit_id: unitId || undefined
      });
      setReportData(response.data || []);
      setGenerated(true);
      toast.success('Laporan berhasil di-generate');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Gagal generate laporan');
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = async (format: 'pdf' | 'excel') => {
    if (!selectedReport) return;
    const toastId = toast.loading(`Mengekspor sebagai ${format.toUpperCase()}...`);
    try {
      const fileData = await reportService.exportReport(selectedReport, format, {
        periode_id: periode !== 'all' ? periode : undefined,
        unit_id: unitId || undefined
      });

      const mimeType = format === 'pdf' ? 'application/pdf' : 'text/csv';
      const fileExtension = format === 'pdf' ? 'pdf' : 'csv';
      
      const blob = new Blob([fileData], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `report-${selectedReport}-${timestamp}.${fileExtension}`);
      
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Export berhasil', { id: toastId });
    } catch (error: any) {
      console.error('Export failed:', error);
      const errorMessage = error.message || 'Gagal mengekspor laporan. Silakan coba lagi.';
      toast.error(errorMessage, { id: toastId });
    }
  };

  const renderChart = () => {
    if (!selectedReport || !generated) return null;

    if (chartType === 'table' || selectedReport === 'overdue') {
      return renderTable();
    }

    if (reportData.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="w-12 h-12 text-neutral-border mb-3" />
          <p className="text-sm text-neutral-text-secondary">Tidak ada data untuk ditampilkan</p>
        </div>
      );
    }

    if (chartType === 'pie') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={reportData}
              cx="50%"
              cy="50%"
              innerRadius={80}
              outerRadius={150}
              paddingAngle={3}
              dataKey="value"
              label={({ name, percent }: { name?: string, percent?: number }) => `${name ?? ''} (${((percent ?? 0) * 100).toFixed(0)}%)`}
            >
              {reportData.map((entry: any, index: number) => (
                <Cell key={`cell-${index}`} fill={entry.fill || COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'line') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={reportData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip contentStyle={{ borderRadius: '12px', fontSize: '12px', border: '1px solid #E5E7EB' }} />
            <Line type="monotone" dataKey="value" stroke="#0f33ab" strokeWidth={3} dot={{ fill: '#0f33ab', r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (selectedReport === 'aktivitas_pelaporan_unit') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={reportData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-15} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip contentStyle={{ borderRadius: '12px', fontSize: '12px', border: '1px solid #E5E7EB' }} />
            <Legend />
            <Bar dataKey="selesai" fill="#22C55E" name="Selesai" radius={[4, 4, 0, 0]} />
            <Bar dataKey="proses" fill="#F59E0B" name="Masih Berjalan" radius={[4, 4, 0, 0]} />
            <Bar dataKey="overdue" fill="#EF4444" name="Melewati Batas Waktu" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (selectedReport === 'kinerja_tindak_lanjut_unit') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={reportData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-15} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip contentStyle={{ borderRadius: '12px', fontSize: '12px', border: '1px solid #E5E7EB' }} />
            <Legend />
            <Bar dataKey="berlangsung" fill="#EAB308" name="Tindak Lanjut Aktif" radius={[4, 4, 0, 0]} />
            <Bar dataKey="selesai" fill="#22C55E" name="Selesai" radius={[4, 4, 0, 0]} />
            <Bar dataKey="menunggu_konfirmasi" fill="#F59E0B" name="Menunggu Konfirmasi" radius={[4, 4, 0, 0]} />
            <Bar dataKey="terhambat" fill="#EF4444" name="Terhambat" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={reportData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-15} textAnchor="end" height={60} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip contentStyle={{ borderRadius: '12px', fontSize: '12px', border: '1px solid #E5E7EB' }} />
          <Bar dataKey="value" radius={[6, 6, 0, 0]}>
            {reportData.map((entry: any, index: number) => (
              <Cell key={`cell-${index}`} fill={entry.fill || COLORS[index % COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderTable = () => {
    if (selectedReport === 'overdue') {
      return (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-neutral-bg">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Kode</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Judul</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Unit</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Risiko</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Terlambat</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">PIC</th>
              </tr>
            </thead>
            <tbody>
              {reportData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-neutral-text-secondary">Tidak ada laporan yang melewati batas waktu</td>
                </tr>
              ) : (
                reportData.map((row, i) => (
                  <tr key={i} className="border-b border-neutral-border hover:bg-danger/5">
                    <td className="px-4 py-3 text-xs font-mono text-teal-main">{row.kode}</td>
                    <td className="px-4 py-3 text-sm font-medium">{row.judul}</td>
                    <td className="px-4 py-3 text-sm text-neutral-text-secondary">{row.unit}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${row.risiko === 'Kritis' ? 'bg-red-100 text-red-700' :
                        row.risiko === 'Tinggi' ? 'bg-orange-100 text-orange-700' :
                          row.risiko === 'Sedang' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                        }`}>{row.risiko}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-danger">{row.hari} hari</td>
                    <td className="px-4 py-3 text-sm text-neutral-text-secondary">{row.pic}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'aktivitas_pelaporan_unit') {
      return (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-neutral-bg">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Unit</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Selesai</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Masih Berjalan</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Melewati Batas Waktu</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Total</th>
              </tr>
            </thead>
            <tbody>
              {reportData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-neutral-text-secondary">Tidak ada data</td>
                </tr>
              ) : (
                reportData.map((row: any, i: number) => (
                  <tr key={i} className="border-b border-neutral-border">
                    <td className="px-4 py-3 text-sm font-medium">{row.name}</td>
                    <td className="px-4 py-3 text-sm text-right text-success font-semibold">{row.selesai}</td>
                    <td className="px-4 py-3 text-sm text-right text-warning font-semibold">{row.proses}</td>
                    <td className="px-4 py-3 text-sm text-right text-danger font-semibold">{row.overdue}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold">{row.total}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'kinerja_tindak_lanjut_unit') {
      return (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-neutral-bg">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Unit PIC</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Selesai</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Tindak Lanjut Aktif</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Menunggu Konfirmasi</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Terhambat</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Rata-rata Progres</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Total</th>
              </tr>
            </thead>
            <tbody>
              {reportData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-neutral-text-secondary">Tidak ada data</td>
                </tr>
              ) : (
                reportData.map((row: any, i: number) => (
                  <tr key={i} className="border-b border-neutral-border">
                    <td className="px-4 py-3 text-sm font-medium">{row.name}</td>
                    <td className="px-4 py-3 text-sm text-right text-success font-semibold">{row.selesai}</td>
                    <td className="px-4 py-3 text-sm text-right text-warning font-semibold">{row.berlangsung}</td>
                    <td className="px-4 py-3 text-sm text-right text-warning font-semibold">{row.menunggu_konfirmasi}</td>
                    <td className="px-4 py-3 text-sm text-right text-danger font-semibold">{row.terhambat}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">{row.avg_progress}%</td>
                    <td className="px-4 py-3 text-sm text-right font-bold">{row.total}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      );
    }

    const total = reportData.reduce((sum: number, r: any) => sum + (r.value || 0), 0);

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-neutral-bg">
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">No</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Nama</th>
              <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Jumlah</th>
              <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Persentase</th>
            </tr>
          </thead>
          <tbody>
            {reportData.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center py-8 text-neutral-text-secondary">Tidak ada data</td>
              </tr>
            ) : (
              <>
                {reportData.map((row: any, i: number) => (
                  <tr key={i} className="border-b border-neutral-border">
                    <td className="px-4 py-3 text-sm text-neutral-text-secondary">{i + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: row.fill || COLORS[i % COLORS.length] }} />
                      {row.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">{row.value}</td>
                    <td className="px-4 py-3 text-sm text-right text-neutral-text-secondary">{total > 0 ? ((row.value / total) * 100).toFixed(1) : 0}%</td>
                  </tr>
                ))}
                <tr className="bg-neutral-bg font-bold">
                  <td className="px-4 py-3 text-sm" colSpan={2}>Total</td>
                  <td className="px-4 py-3 text-sm text-right">{total}</td>
                  <td className="px-4 py-3 text-sm text-right">100%</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const periodeOptions = [
    { value: 'all', label: 'Semua Periode' },
    ...periodes.map(p => ({ value: String(p.id), label: p.nama }))
  ];

  const unitOptions = [
    { value: '', label: 'Semua Unit' },
    ...units.map(u => ({ value: String(u.id), label: u.name }))
  ];

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Laporan & Analitik' },
      ]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Laporan & Analitik</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Buat laporan dengan visualisasi data dinamis</p>
        </div>
      </div>

      {/* Info Banner untuk Dekan/Kalem */}
      {(role.isDekan || role.isKalem) && (
        <div className="p-3 bg-info/5 border border-info/20 rounded-xl flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-info shrink-0" />
          <p className="text-xs text-info">
            Anda hanya dapat melihat data untuk <strong>{user?.unit?.name}</strong> dan unit di bawahnya.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          <Card>
            <h3 className="text-sm font-bold text-teal-dark mb-3">1. Pilih Jenis Laporan</h3>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {reportConfigs.map((config) => (
                <button
                  key={config.type}
                  onClick={() => {
                    setSelectedReport(config.type);
                    setGenerated(false);
                    setChartType(config.supportedCharts[0]);
                  }}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl text-left transition-all ${selectedReport === config.type
                    ? 'bg-teal-main/5 border-2 border-teal-main'
                    : 'border-2 border-transparent hover:bg-neutral-bg'
                    }`}
                >
                  <div className={`p-2 rounded-lg shrink-0 ${selectedReport === config.type ? 'bg-teal-main text-white' : 'bg-neutral-bg text-neutral-text-secondary'
                    }`}>
                    {config.icon}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-neutral-text-primary">{config.label}</p>
                    <p className="text-xs text-neutral-text-secondary mt-0.5">{config.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-teal-dark">2. Filter</h3>
              {reportExplanation && (
                <InfoTooltipButton
                  title={reportExplanation.title}
                  content={reportExplanation.content}
                />
              )}
            </div>
            {loadingFilters ? (
              <p className="text-sm text-neutral-text-secondary"><LoadingLabel label="Memuat filter..." /></p>
            ) : (
              <div className="space-y-3">
                <Select
                  label="Periode"
                  options={periodeOptions}
                  value={periode}
                  onChange={setPeriode}
                />
                <Select
                  label="Unit (Opsional)"
                  options={unitOptions}
                  value={unitId}
                  onChange={setUnitId}
                />
              </div>
            )}
          </Card>

          {selectedReport && currentConfig && (
            <Card>
              <h3 className="text-sm font-bold text-teal-dark mb-3">3. Tampilan</h3>
              <div className="grid grid-cols-2 gap-2">
                {currentConfig.supportedCharts.map((ct) => (
                  <button
                    key={ct}
                    onClick={() => setChartType(ct)}
                    className={`flex items-center gap-2 p-2.5 rounded-lg text-sm font-medium transition-all ${chartType === ct
                      ? 'bg-teal-main text-white'
                      : 'bg-neutral-bg text-neutral-text-secondary hover:bg-neutral-border'
                      }`}
                  >
                    {ct === 'bar' && <BarChart3 className="w-4 h-4" />}
                    {ct === 'pie' && <PieChartIcon className="w-4 h-4" />}
                    {ct === 'line' && <BarChart3 className="w-4 h-4" />}
                    {ct === 'table' && <Table className="w-4 h-4" />}
                    {ct === 'bar' ? 'Batang' : ct === 'pie' ? 'Donat' : ct === 'line' ? 'Garis' : 'Tabel'}
                  </button>
                ))}
              </div>
            </Card>
          )}

          <Button
            variant="primary"
            className="w-full"
            onClick={handleGenerate}
            icon={<Play className="w-4 h-4" />}
            disabled={!selectedReport}
            loading={loading}
          >
            Generate Laporan
          </Button>
        </div>

        <div className="lg:col-span-2">
          {!generated ? (
            <Card className="flex flex-col items-center justify-center py-20 text-center">
              <BarChart3 className="w-16 h-16 text-neutral-border mb-4" />
              <h3 className="text-lg font-bold text-neutral-text-primary">Pilih & Generate Laporan</h3>
              <p className="text-sm text-neutral-text-secondary mt-1 max-w-md">
                Pilih jenis laporan di panel kiri, atur filter, lalu klik "Generate Laporan" untuk melihat hasil.
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-bold text-teal-dark">{currentConfig?.label}</h2>
                      {reportExplanation && (
                        <InfoTooltipButton
                          title={reportExplanation.title}
                          content={reportExplanation.content}
                        />
                      )}
                    </div>
                    <p className="text-xs text-neutral-text-secondary mt-0.5">
                      Di-generate: {new Date().toLocaleString('id-ID')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleExport('excel')} icon={<FileSpreadsheet className="w-4 h-4" />}>
                      Excel
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} icon={<Download className="w-4 h-4" />}>
                      PDF
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handlePrint} icon={<Printer className="w-4 h-4" />}>
                      Print
                    </Button>
                  </div>
                </div>

                <div className="min-h-[400px]">
                  {renderChart()}
                </div>
              </Card>

              {selectedReport !== 'overdue' && chartType !== 'table' && (
                <Card padding="sm">
                  <h3 className="text-sm font-bold text-teal-dark mb-3">Ringkasan Data</h3>
                  {renderTable()}
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportBuilder;
